/** Everything the Feed, Saved and Home screens need to make sense of a row
 *  from the `feed` view. Kept out of the screens because all three read the
 *  same rows and were otherwise going to disagree about what a date means.
 */
import { D, addDays, fmt, iso } from './calendar'
import { fmtDayShort } from './today'
import { daylongHasEnded } from './daylong.ts'
import { clockIn, dayIn, zoneOfCity } from './zone.ts'
import type { FeedRow } from './types'
import { filmRank, runShape } from './filmRank'
import { creditFor, ownPictureUsable, type Pictures } from './attribution'
import { sentences } from './exhibitionText'
import { classicalTitle } from './classicalForm'
import { artistNames, clockAt } from './exhibitionCard'
import { isEnglishTalk, TALK_LIKE_TYPES } from './talkLang'
import { idsOf as affinityIds } from './affinity'
import type { Chip } from './entities'

// The film ranking rule lives in its own file because it has no dependencies
// and the Worker's test suite can therefore load it directly — the app's
// extensionless imports are a Vite convenience that Node's loader does not
// share, and this is the one piece of app logic worth testing offline.
export { filmRank, runShape, type RunShape } from './filmRank'
// Phase 6c: the same reason — `filmGroupKey` and `versionTags` are pure and
// live in filmRank.ts so the Worker suite can test them directly.
export { filmGroupKey, versionTags, type Version } from './filmRank'
import { filmGroupKey } from './filmRank'
import { aheadCount, mergePlaces } from './places'
import { priceFact } from './rowMeta'

// ---------------------------------------------------------------------------
// When
// ---------------------------------------------------------------------------

/**
 * The wall-clock time of one of `e`'s showings, in the row's own zone (C2.1:
 * its town's region's, `zone.ts`), or null when there is no usable one.
 *
 * `showings.starts_at` is not null, so an extractor that found a date and no
 * time still has to write something, and what it writes is local midnight.
 * Printing "Sun 20 Sept, 00:00" claims a precision the source never had — and
 * a real midnight start is rare enough that losing it costs less than
 * inventing one.
 */
function localTime(e: FeedRow, at: string | null): string | null {
  if (!at) return null
  const t = clockIn(at, zoneOfCity(e.city))
  return t === '00:00' || t === '24:00' ? null : t
}

/** What to print in the one line a card has for time.
 *
 *  A showing is a datetime and carries the hour, which is the useful part for
 *  anything happening once. A run is two dates and the hour means nothing. */
export function whenText(e: FeedRow): string {
  if (e.published_on) return fmt(e.published_on, { day: 'numeric', month: 'short' })
  if (!e.run_start) return 'No dates given'

  const oneDay = !e.run_end || e.run_end === e.run_start
  if (oneDay) {
    const day = fmtDayShort(e.run_start)
    // Only trust the clock when there is exactly one showing: with several, the
    // first one is not "the" time, and the card would state it as if it were.
    //
    // Phase 7b: `showing_count` counts performances now, so an `opening`
    // entry — whose one showing is the party — counts 0 and lost its hour
    // here. `clockAt` is that rule, in a file the offline suite can load.
    // Cards WP4: `aheadCount`, so a film counts its future showings over the
    // set rather than `showing_count`, which keeps a film's past weeks.
    const t = localTime(e, clockAt(aheadCount(e), e.next_showing, e.next_opening))
    return t ? `${day}, ${t}` : day
  }

  const a = fmt(e.run_start, { day: 'numeric', month: 'short' })
  const b = fmt(e.run_end!, { day: 'numeric', month: 'short' })
  return `${a} – ${b}`
}

/**
 * The second line of a card, after the date and the place.
 *
 * DIRECTION's phase 7 asks for four different cards and they are four
 * different facts, not four layouts:
 *
 *   a concert   the conductor and the programme, which `selection.md` says the
 *               decision is actually made on — "programme alone is not enough"
 *   a film      the cinema of the NEXT showing, which is rarely the one the
 *               row is filed under
 *   a play      the director, and the surtitle statement where there is one
 *   an exhibition  nothing: the date range already said it
 *
 * Returns null when there is nothing worth a second line, because an empty
 * line of punctuation is worse than no line.
 */
export function detailLine(e: FeedRow): string | null {
  const p = e.performers ?? {}

  if (e.discipline === 'film' || e.type === 'screening') {
    // Only when it differs from the row's own venue — otherwise the card would
    // print the same cinema twice.
    const cinema = e.next_showing_venue && e.next_showing_venue !== e.venue ? e.next_showing_venue : null
    const when = localTime(e, e.next_showing)
    const bits = [cinema, when && cinema ? `at ${when}` : when ? `next at ${when}` : null].filter(Boolean)
    const facts = [p.director, e.film?.year].filter(Boolean).join(' · ')
    return [bits.join(' '), facts].filter(Boolean).join(' · ') || null
  }

  if (e.type === 'concert') {
    return [p.conductor, p.ensemble, p.programme].filter(Boolean).join(' · ') || null
  }

  if (e.type === 'performance') {
    return [p.director, p.composer].filter(Boolean).join(' · ') || null
  }

  return null
}

/**
 * The artists, for the line under an exhibition's title. Phase 7b.
 *
 * Phase 7a put them in `exhibition.artists` — 87% filled, and shown nowhere.
 * Doug cares about the opening party and the artists, in that order, and a
 * gallery card that prints only a title and a date range says neither.
 *
 * Three then "+N": a group show lists eleven names and a card is not a wall
 * label. Falls back to `performers.director`, which is where they lived
 * before 7a renamed the field and where they still are on any row whose
 * recipe has not re-harvested since.
 */
export function artistLine(e: FeedRow): string | null {
  // Only where "artists" means artists. `performers.director` below is the
  // film director on a screening and the stage director on a play, and this
  // line is read by the compact shape, which carries both — the one-value-
  // two-jobs bug the Phase 7a rename was undoing in the first place.
  if (e.type !== 'exhibition' && e.type !== 'opening') return null

  const from = e.exhibition?.artists?.length
    ? e.exhibition.artists
    : (e.performers?.director ?? '').split(/\s*[,;]\s*/)

  return artistNames(from)
}

/**
 * What a remaining `type = 'opening'` entry is, and when. Phase 7b rule 6.
 *
 * The rule asks for "Opening · Thu 18:00 · <venue>", and the compact card
 * says all three — but not all three in this line. Its date block carries
 * "FRI / 25 / SEPT / 16:00" in the column to the left and `Head` prints the
 * venue directly underneath, so a line repeating them puts the same three
 * facts on screen twice, 40px apart, on a 390px phone. What no part of that
 * card said before is the one word this adds: the row is a party, not
 * another exhibition.
 *
 * So: the word, and nothing the card has already said. A version of this
 * that put the hour back "where the date block has none" would be dead
 * code — both read `clockAt`, so the box has an hour exactly when there is
 * one to have.
 */
export function openingLine(e: FeedRow): string | null {
  return e.type === 'opening' ? 'Opening' : null
}

/**
 * Phase 4b: a concert's programme, read once and shared by the card and the
 * sheet, so the two cannot disagree about which piece is the main one.
 *
 * `e.classical` when the normaliser has run for this row: `others` is every
 * programme item but the main one, in order, canonical title where the
 * reference matched and the source's own where it did not — the same rule
 * `classicalTitle` uses for the headline, so `main` here is exactly its
 * `work`. Falls back to `performers` where `classical` is still null (Phase
 * 4a has not re-harvested this row's recipe yet): `others` is then the whole
 * printed programme, unfiltered, because there is no `is_main` to exclude by
 * — the caller already knows how to keep its own lead out of that list, the
 * way `CardBody`'s programme shape did before this phase.
 */
export function programmeLines(e: FeedRow): {
  main: string | null
  others: string[]
  ensemble: string | null
  tier: 'major' | 'youth' | null
  conductor: string | null
  soloists: string[]
} {
  const main = classicalTitle(e)?.work ?? null
  const c = e.classical

  if (c) {
    return {
      main,
      others: c.programme.filter(i => !i.is_main).map(i => i.work ?? i.work_src),
      ensemble: c.ensemble?.name ?? null,
      tier: c.ensemble?.tier ?? null,
      conductor: c.conductor,
      soloists: c.soloists.map(s => (s.instrument ? `${s.name}, ${s.instrument}` : s.name)),
    }
  }

  const p = e.performers ?? {}
  return {
    main,
    others: (p.programme ?? '').split(/\s*;\s*/).map(w => w.trim()).filter(Boolean),
    ensemble: p.ensemble ?? null,
    tier: null,
    conductor: p.conductor ?? null,
    soloists: p.soloists ? [p.soloists] : [],
  }
}

/**
 * Phase 5b: a stage row's own facts, read once and shared by the card and
 * the sheet — the same reason `programmeLines` exists for a concert.
 *
 * `headline` is "Composer · Work" for an opera (canonical where `stage`
 * matched one, the printed name otherwise — `stage.composer`/`.work` are
 * already that, there is no separate `_src` to fall back to) or the
 * playwright/choreographer for anything else. `company` falls back to the
 * house's own name — `organisations.name` for an opera house, the venue for
 * a theatre with no organisation row — only when the row names no guest
 * company of its own; that fallback belongs here and not in `stage.ts`,
 * which has no venue or organisation to look either up in.
 *
 * Falls back to `performers` where `stage` is still null (this recipe has
 * not re-harvested since Phase 5b), the same fallback `programmeLines` uses.
 */
export function stageLines(e: FeedRow): {
  headline: string | null
  company: string | null
  conductor: string | null
  director: string | null
  isPremiere: boolean
  reviewCount: number
} {
  const s = e.stage
  if (s) {
    const headline = e.discipline === 'opera'
      ? [s.composer, s.work].filter(Boolean).join(' · ') || null
      : s.author
    return {
      headline,
      company: s.company ?? e.organisation ?? e.venue ?? null,
      conductor: s.conductor,
      director: s.director,
      isPremiere: s.is_premiere,
      reviewCount: s.reviews?.length ?? 0,
    }
  }
  const p = e.performers ?? {}
  return {
    headline: p.composer ?? null,
    company: p.ensemble ?? e.organisation ?? null,
    conductor: null,
    director: p.director ?? null,
    isPremiere: false,
    reviewCount: 0,
  }
}

/** The card's second line for a stage row: company · cond. X · dir. Y. */
export function stageWho(sl: ReturnType<typeof stageLines>): string | null {
  return [sl.company, sl.conductor && `cond. ${sl.conductor}`, sl.director && `dir. ${sl.director}`]
    .filter(Boolean)
    .join(' · ') || null
}

/**
 * Who is in it, in one line — WP 3.1's lead card, and nothing else on Today.
 *
 * The one thing a title does not say and the decision is actually made on:
 * `selection.md`'s ranking half runs on composer, orchestra and a named
 * conductor or soloist, so a lead card printing only "Sonderkonzert" is
 * hiding the fact it is being led with. Names in the order you would say
 * them — who is in front, who is playing, whose orchestra it is.
 *
 * Every part of it is a field of the row. This is not a sentence: it is the
 * same join `stageWho` already does, extended to the three other kinds of
 * row that carry people. H2 removed the written "why" from this card and
 * this is what the card says instead — what the thing is.
 *
 * The instrument is dropped from a soloist here and kept in
 * `programmeLines`: the sheet has room for "Víkingur Ólafsson, piano" and
 * this line joins with commas, so carrying it would make "piano" read as a
 * fourth performer.
 */
export function whoLine(e: FeedRow): string | null {
  if (e.type === 'concert') {
    const c = e.classical
    const names = c
      ? [c.conductor, ...c.soloists.map(s => s.name), c.ensemble?.name]
      : [e.performers?.conductor, e.performers?.soloists, e.performers?.ensemble]
    return newNames(names, e.title)
  }

  if (e.type === 'performance' || e.discipline === 'opera'
      || e.discipline === 'theatre' || e.discipline === 'dance') {
    return stageWho(stageLines(e))
  }

  // An exhibition or an opening names its artists; anything else that carries
  // people at all carries them under `performers`.
  const artists = artistLine(e)
  if (artists) return artists

  const p = e.performers ?? {}
  return [p.director && `dir. ${p.director}`, p.cast, p.ensemble].filter(Boolean).join(', ') || null
}

/**
 * The names the title has not already said.
 *
 * Half the Konzerthaus's rows are titled "Konzerthausorchester Berlin, Iván
 * Fischer", and printing the performers under that gives the card the same
 * two names twice, 20px apart — which is the first thing the render showed.
 * Dropping the ones already up there leaves the soloist, which is the line's
 * whole reason for existing. Nothing left to add is no line, not an empty
 * one.
 *
 * Substring matching, and safe to be: these are proper names being looked for
 * in the row's own title, not German compounds — the trap CLAUDE.md names is
 * `Führung` inside `Entführung`, and no conductor is a substring of another
 * conductor in a way that would matter here.
 */
function newNames(names: (string | null | undefined)[], title: string): string | null {
  const t = title.toLowerCase()
  return names.filter((n): n is string => !!n && !t.includes(n.toLowerCase())).join(', ') || null
}

/** "1 h 45 min" from whole minutes. Phase 4b's own rule, shared by
 *  `sheetFacts` and `ics.ts`'s own copy — `ics.ts` cannot import this: it has
 *  to stay dependency-free so the Worker suite can load it (see its header),
 *  so the two are deliberately the same three lines twice rather than one
 *  file the other cannot reach. */
export function formatDuration(min: number): string {
  const h = Math.floor(min / 60)
  const m = min % 60
  if (h && m) return `${h} h ${m} min`
  if (h) return `${h} h`
  return `${m} min`
}

/** "Sat, 17 Oct · 18:00" for the sheet's own Opening fact — the same
 *  day/time split `CardBody`'s run shape draws for the card, shared here so
 *  the two cannot say something different about the same party. Both halves
 *  in the row's own zone. */
export function openingWhen(e: FeedRow): string | null {
  if (!e.next_opening) return null
  const day = fmtDayShort(dayIn(e.next_opening, zoneOfCity(e.city)))
  const t = localTime(e, e.next_opening)
  return t ? `${day} · ${t}` : day
}

export interface Fact {
  label: string
  value: string
  href?: string
  /** Phase E5. Set by `EventSheet` on the rows below that name people, once
   *  `feed_detail` has answered; the `<dd>` then holds chips instead of
   *  `value`.
   *
   *  Not set by `sheetFacts` itself, which is the shape change the brief
   *  offered a choice about. `sheetFacts` reads a `FeedRow` — `feed_lite`,
   *  whose `people` is `[{id, role}]` with no name on it (K11) — so it has
   *  nothing to build a chip from, and rule 2 needs its string to stay as the
   *  `<dd>`'s content while `feed_detail` is in flight. So the row keeps its
   *  text and gains the chips beside it, rather than `{ label, chips }`
   *  replacing `{ label, value }`. */
  chips?: Chip[]
}

/**
 * Which `sheetFacts` row each role's chips belong in.
 *
 * Rule 1: the people rows stay where they are, with the labels they already
 * have — `Orchestra`, not `Ensemble`, on a concert that has said Orchestra
 * since Phase 4b. A role with no row here (a concert's composers, an opera's
 * ensemble) gets a new row after the last one that matched, labelled by
 * `ROLE_LABEL`.
 *
 * Keyed by `entry_people.role` and `entry_ensembles.role`, valued with the
 * exact strings `sheetFacts` above pushes. It lives beside `sheetFacts`
 * because renaming a label there without touching this is how the sheet would
 * end up drawing both a `Composer` text row and a `Composers` chip row.
 */
export const PEOPLE_FACT_LABEL: Record<string, string> = {
  composer: 'Composer',
  author: 'Author',
  artist: 'Artists',
  conductor: 'Conductor',
  director: 'Director',
  curator: 'Curators',
  performer: 'Orchestra',
}

/**
 * Phase 9: the sheet's kind block, read once rather than as a wall of inline
 * `<dt>`s that had grown one at a time since Phase 4b, each its own small
 * chance to disagree with the card about a name or an order.
 *
 * Kind rows first, in the type's own order; the common rows — When, Where,
 * Tickets, Language — always last, because a fact about this row in
 * particular is worth more of the little room a sheet has than the four
 * every kind shares. Rule 1: a row appears only when it has a value — Where,
 * Tickets and Language included since UI-audit a1-31 (Doug chose "show only
 * facts that are known"): "Not given" and "Unclear" are rows of ignorance,
 * and a sheet of them says nothing. A row with none known gets no facts.
 */
export function sheetFacts(e: FeedRow, home: Point | null, current: ReachRegion, towns: readonly ReachTown[]): Fact[] {
  const out: Fact[] = []
  const push = (label: string, value: string | null | undefined) => {
    if (value) out.push({ label, value })
  }

  if (e.type === 'concert') {
    const pg = programmeLines(e)
    push('Orchestra', pg.ensemble)
    push('Conductor', pg.conductor)
    push('Soloists', pg.soloists.join(', '))
    if (e.classical?.duration_min != null) push('Duration', formatDuration(e.classical.duration_min))
  }

  if (e.type === 'performance') {
    const s = e.stage
    push('Work', s?.work)
    if (s?.composer || s?.author) push(s.composer ? 'Composer' : 'Author', s.composer ?? s.author)
    push('Company', s?.company ?? e.organisation)
    push('Orchestra', s?.orchestra)
    push('Conductor', s?.conductor)
    push('Director', s?.director)
    if (s && (s.is_premiere || s.premiere_on)) {
      push('Premiere', s.premiere_on ? fmt(s.premiere_on, { day: 'numeric', month: 'short', year: 'numeric' }) : 'new production')
    }
    if (s?.duration_min != null) push('Duration', formatDuration(s.duration_min))
    if (s && s.cast.length > 0) {
      const names = s.cast.slice(0, 6).map(c => c.name).join(', ')
      push('Cast', s.cast.length > 6 ? `${names} +${s.cast.length - 6}` : names)
    }
  }

  if (e.type === 'exhibition' || e.type === 'opening') {
    push('Artists', artistLine(e))
    push('Curators', e.exhibition?.curators.join(', '))
    push('Opening', openingWhen(e))
    push('Hours', e.hours_src)
    push('Admission', e.exhibition?.admission_src)
  }

  if (e.type === 'screening' || e.discipline === 'film' || e.type === 'release') {
    if (typeof e.film?.runtime_min === 'number') push('Runtime', formatDuration(e.film.runtime_min))
    push('Certification', e.film?.certification ? `FSK ${e.film.certification}` : null)
    push('Original title', e.title_original && e.title_original !== e.title ? e.title_original : null)
    if (e.type === 'release' && e.run_start) {
      push('Release (DE)', fmt(e.run_start, { day: 'numeric', month: 'short', year: 'numeric' }))
    }
    const scores = filmScores(e)
    if (scores.length) push('Ratings', scores.map(s => `${s.label} ${s.value}`).join(' · '))
  }

  // Phase 8a: set only on a promoted newsletter lead, whatever kind it
  // otherwise is — a festival at Markthalle Neun still names its organiser.
  if (e.newsletter) {
    push('Organiser', e.newsletter.organiser !== e.venue ? e.newsletter.organiser : null)
    push('Source', newsletterSource(e))
  }

  push('When', whenText(e))
  const where = [e.venue, e.street, e.postcode].filter(Boolean).join(', ')
  // UI-audit a1-11: a promoted newsletter lead whose place matched no venue
  // row keeps it as the mail wrote it, and the head already prints that
  // (`rowMeta.ts`'s `placeName`) — so Where says it too rather than "Not
  // given" under a header naming Trabrennbahn Karlshorst.
  const mailed = e.venue_id == null ? e.newsletter?.place_src ?? null : null
  push('Where', where || mailed || e.organisation)
  // Blind spots A3: a day trip says how far, straight under where it is.
  push('Trip', tripLine(e, home, current, towns))
  push('Tickets', priceText(e))
  push('Language', knownLanguage(e))

  return out
}

/**
 * How far ahead "soon" reaches, in days.
 *
 * DIRECTION's own number: its refresh table has season publishers re-pulled
 * "monthly, next eight weeks only", because eight weeks is the distance at
 * which a Berlin concert stops being a plan and starts being a note. The feed
 * carries ten months — 582 of today's 1,418 rows are past this line — and
 * without the fold a high-scoring May concert sits above this weekend, which
 * is the thing DIRECTION asks phase 7 to stop.
 */
export const NEAR_DAYS = 56

/** Is this close enough to decide about now?
 *
 *  An undated row counts as near. It has no date to be far with, and hiding it
 *  behind "further out" would file "no date given" under a claim about time. */
export function isNear(e: FeedRow, today: string): boolean {
  const start = e.run_start ?? e.published_on
  if (!start) return true
  // A run that has already begun is on now, however long it lasts.
  if (start <= today) return true
  return start <= addDays(today, NEAR_DAYS)
}

/** The date an entry sorts on. News sorts on publication, everything else on
 *  when it starts. Undated rows sort last rather than first. */
export const sortDate = (e: FeedRow) => e.run_start ?? e.published_on ?? '9999-12-31'

/** Is this on, or still to come, on a given day. */
export const runsOn = (e: FeedRow, day: string) =>
  !!e.run_start && e.run_start <= day && (e.run_end ?? e.run_start) >= day

/** Does the run overlap a closed range — the test a trip needs. */
export const overlaps = (e: FeedRow, from: string, to: string) =>
  !!e.run_start && e.run_start <= to && (e.run_end ?? e.run_start) >= from

/** Saturday and Sunday of the weekend "today" is heading into or already in —
 *  a Monday-through-Friday today gets the coming one, a Saturday gets itself
 *  plus tomorrow, and a Sunday gets just itself: Saturday has already gone
 *  (UI-audit F21, "what is on tonight or this weekend"). */
export function weekendRange(today: string): { from: string; to: string } {
  const dow = D(today).getUTCDay() // 0 = Sunday .. 6 = Saturday
  if (dow === 0) return { from: today, to: today }
  const sat = addDays(today, (6 - dow + 7) % 7)
  return { from: sat, to: addDays(sat, 1) }
}

/** The window an event can be put in the calendar for: inside its own run, and
 *  never in the past. Returns null when the run has already finished. */
export function calendarWindow(e: FeedRow, today: string): { min: string; max: string } | null {
  if (!e.run_start) return null
  const max = e.run_end ?? e.run_start
  if (max < today) return null
  return { min: e.run_start > today ? e.run_start : today, max }
}

// ---------------------------------------------------------------------------
// Language
// ---------------------------------------------------------------------------

export const EN_LABEL: Record<string, [string, string]> = {
  native: ['In English', 'en'],
  surtitles: ['Subtitles', 'sur'],
  partial: ['Some English', 'sur'],
  none: ['German only', 'de'],
  not_applicable: ['No language barrier', 'en'],
  unknown: ['Unclear', 'unk'],
}

export const enLabel = (e: FeedRow) => EN_LABEL[e.english_access] ?? EN_LABEL.unknown

/** The Language fact, or null where nobody knows — `unknown`, or a value
 *  this table has no words for. "Unclear" is a label for a card's warning
 *  tag (`enWarning`), not a fact a sheet states (UI-audit a1-31). */
// `Object.hasOwn`, not `EN_LABEL[x]`: a plain object answers `constructor`
// out of its prototype (CLAUDE.md's tab-alias bug), and the lookup would
// decide both whether a value is known and what it says.
export const knownLanguage = (e: FeedRow): string | null =>
  e.english_access && e.english_access !== 'unknown' && Object.hasOwn(EN_LABEL, e.english_access)
    ? EN_LABEL[e.english_access][0] : null

/**
 * The language tag a card should carry, or null for nothing.
 *
 * A tag on every row is a tag nobody reads. "No language barrier" is on 23 of
 * 47 rows and says only that an exhibition is an exhibition; "In English" is
 * good news and needs no flag either. What is worth interrupting for is doubt:
 * subtitles rather than the real thing, partial English, or an extractor that
 * could not tell. German-only never reaches a card for a profile with `english_only` — see `excludedAccess`.
 */
export function enWarning(e: FeedRow): [string, string] | null {
  if (e.english_access === 'native' || e.english_access === 'not_applicable') return null
  return enLabel(e)
}

/**
 * Language levels that never appear in the feed at all, for a profile with
 * `english_only` (the column's default, and Doug's value).
 *
 * A hard exclusion rather than a filter default, because a filter you have to
 * remember to set is one you will eventually forget. Reverting is deleting a
 * string from this array — or, now, a person turning `english_only` off,
 * which hides nothing (U1.2: this was a constant, `['none']`, for
 * everyone, one person's preference written as a constant).
 *
 * `unknown` deliberately stays in: it is a third of the data, and hiding it
 * would hide most of the feed to avoid a language problem that may not exist.
 */
export const excludedAccess = (englishOnly: boolean): string[] => (englishOnly ? ['none'] : [])

export const languageAllowed = (e: FeedRow, englishOnly: boolean) =>
  !excludedAccess(englishOnly).includes(e.english_access)

/**
 * WP 6.3: talks and tours Doug cannot follow are dropped outright, ahead of
 * `languageAllowed`'s own, gentler rule. A German `Führung` with
 * `english_access: 'unknown'` still passes `languageAllowed` — that column
 * only excludes the confirmed-German-only third — but a talk this codebase
 * cannot read as English belongs nowhere Doug looks, which is what the
 * "Talks only in English" chip promises.
 *
 * `isEnglishTalk` (`talkLang.ts`, WP 6.0/6.6) is the one place that reads
 * `english_access`, `event_lang` and the title's own stopword count to
 * answer that; this only ever gates a row through it, and says yes to
 * everything else — the same shape `languageAllowed` is, one line every
 * screen shares rather than three call sites that could drift.
 *
 * Which types that gate applies to is `talkLang.ts`'s own `TALK_LIKE_TYPES`
 * — read here rather than restated, after a bug where this line already
 * knew about `tour` and `isEnglishTalk`'s own, separate type check did not,
 * so every tour was silently dropped everywhere despite this file looking
 * right. One set now answers "which types" for both places; this line only
 * asks it the question.
 *
 * U1.2: `talks` is `profiles.talks_language`. Only `'en'` (the default, and
 * Doug's value) gates; `'de'` and `'any'` let every talk through, because a
 * German reader loses nothing to an English talk and the gate exists to drop
 * what the reader cannot follow, not to prefer a language.
 */
export const talkLanguageAllowed = (e: FeedRow, talks: string) =>
  talks !== 'en' || !TALK_LIKE_TYPES.has(e.type) || isEnglishTalk(e)

// ---------------------------------------------------------------------------
// Geography
//
// A quarter of what arrives is not Berlin: Dresden and London turning up
// inside Berlin newsletters, which no source-level filter can catch.
//
// This used to be a hardcoded list of the eight organisations known to be
// elsewhere, because `organisations.city` read 'Berlin' for every row — the
// column default, never set by anything. Migration 005 dropped that default,
// backfilled the rows that were wrong, and put `city` on the entry and in the
// view, so the answer now comes from the data: 40 Berlin, 10 Dresden, 2
// London.
//
// Null is a real answer and means the extractor did not say. It is
// deliberately not folded into Berlin — that conflation is the whole bug this
// replaced — so `cityOf` returns null and each caller decides what to do with
// not knowing.
// ---------------------------------------------------------------------------

import { reachOf, tripLine, type Point, type ReachRegion, type ReachTown } from './reach'
export {
  reachOf, regionOf, type Reach, type ReachRegion, type ReachTown, type Point, distanceKm,
  travelEstimateMin, tripLine,
} from './reach'

/** Close enough to tag "near you" rather than just sorting first — within
 *  the profile's `near_km` (2.5 by default: the distance a Kreuzberg/Neukölln
 *  evening still feels local at). Null (no coordinates, or no home to measure
 *  from) is never near — a venue nobody has geocoded gets no tag, not a
 *  false one. */
export const nearHome = (km: number | null, nearKm: number): boolean => km != null && km <= nearKm

/** Where a row is. The view resolves this — `COALESCE(v.city, e.city,
 *  o.city)`, the building before the source — so there is nothing to
 *  correct here. */
export const cityOf = (e: FeedRow): string | null => e.city ?? null

/** In the city of the region being looked at (`current`, C2.1): a `core`
 *  town of it, or — while that region is Berlin — no city at all (T2). */
export const isHere = (e: FeedRow, current: ReachRegion, towns: readonly ReachTown[]) =>
  reachOf(cityOf(e), current, towns) === 'city'

/** A show worth a Saturday rather than an evening. Doug's steer, 21
 *  September: Potsdam, Brandenburg and Dresden are day trips and belong to
 *  a weekend rather than to the feed's normal flow. Blind spots A3 draws the
 *  mark: `tripLine` on the card and sheet, Today's Worth a drive, Browse's
 *  Day trips. A day trip of the region being looked at. */
export const isDayTrip = (e: FeedRow, current: ReachRegion, towns: readonly ReachTown[]) =>
  reachOf(cityOf(e), current, towns) === 'day_trip'

/**
 * Geography for the feed: the region being looked at and its day trips,
 * anywhere a stay overlaps the dates you would actually be there, and —
 * from Berlin — anything whose city nobody recorded (T2).
 *
 * Unknown is included rather than hidden. Hiding it would be guessing in the
 * direction that loses events, and the whole point of separating null from
 * Berlin is to stop guessing — an entry the extractor could not place is a
 * data problem to fix, not a thing to silently drop.
 */
export function inReach(
  e: FeedRow,
  trips: { label: string | null; starts_on: string; ends_on: string }[],
  current: ReachRegion,
  towns: readonly ReachTown[],
) {
  const city = cityOf(e)
  const reach = reachOf(city, current, towns)

  // Home, or close enough to go and come back. Unknown counts as home for
  // the reason above: hiding it would be guessing in the direction that
  // loses events.
  if (reach === 'city' || reach === 'day_trip') return true

  // Somewhere he is actually going. London is the case that made this
  // matter — a Tate show was in the queue and would never have been
  // reachable.
  return trips.some(
    t => (t.label ?? '').toLowerCase() === (city ?? '').toLowerCase() && overlaps(e, t.starts_on, t.ends_on),
  )
}

// ---------------------------------------------------------------------------
// Lanes
//
// From taste/selection.md: quotas rather than one ranked list, because a
// high-volume category otherwise starves a low-volume one. An empty lane is
// drawn empty on purpose — it is how it stays obvious which source is missing.
// ---------------------------------------------------------------------------

export interface Lane {
  id: string
  label: string
  /** How many to surface before the lane has to be expanded. */
  quota: number
  match: (e: FeedRow) => boolean
  /** Said in the empty state, when the lane has nothing in it. */
  missing: string
  /** This lane carries no score, so "best" cannot mean "highest".
   *
   *  Doug's call, 20 September: theatre is not scored, because there is not
   *  enough evidence in the profile to rank it. That leaves `score` null on
   *  every Stage row, and `best` subtracting nulls is a tie — so the quota
   *  would surface whichever two the database happened to return. The soonest
   *  two is the honest answer for an evening you either can or cannot attend.
   *  See `notRanked` in src/score.ts. */
  unranked?: boolean
  /** Claimed but never drawn.
   *
   *  Club and Gigs were hidden this way until the blind-spots build gave
   *  them sources and scores; today only `GALLERY_MAIL` is. Hiding has to
   *  claim as well as hide: a row no lane claims falls into Everything else,
   *  which is more visible than the lane it was kept out of, not less. */
  hidden?: boolean
  /** Drawn in Browse and on the place page, never in the Feed. Not `hidden`,
   *  which Browse and the place page both filter on: one flag for "nowhere"
   *  and another for "not on the front page", because gallery mail is the
   *  second and would vanish from Browse if it borrowed the first. */
  offFeed?: boolean
}

const is = (e: FeedRow, types: string[], disciplines: string[] = []) =>
  types.includes(e.type) || (!!e.discipline && disciplines.includes(e.discipline))

/** The types `notRanked` leaves unscored whatever their discipline. */
const UNSCORED_TYPES = ['exhibition', 'opening', 'release']

/** A gig's disciplines: a concert that is not classical (README,
 *  *Categories*). `browse.ts`'s `kindOf` and `rowMeta.ts`'s `kindWord`
 *  restate it for their no-imports rule. */
export const GIG_DISCIPLINES = ['rock_pop', 'electronic', 'jazz']

/** N3's tag on everything a gallery sender's mail became. `tags` is `[]` on
 *  every row (`feed_lite`), but `?.` costs nothing against a row cached
 *  before a field existed (the E5 trap). */
const galleryTagged = (e: FeedRow) => !!e.tags?.includes('gallery-mail')

export const LANES: Lane[] = [
  {
    id: 'orchestral', label: 'Orchestral', quota: 3,
    // Not a show, an opening or a release, even about music: those three are
    // unscored (`notRanked`), and Orchestral sorts by score, so a Staatsoper
    // costume show tagged `opera` would sort last here with nothing to sort
    // by. Their own lanes below claim them instead; Film has the same guard.
    // `test/lanes.test.ts` holds every type × discipline to this.
    match: e => is(e, [], ['classical', 'opera']) && !UNSCORED_TYPES.includes(e.type),
    missing: 'No classical has been extracted yet.',
  },
  {
    /**
     * Openings, which are a different decision from the show they open.
     *
     * Doug: the thing he cares about most, and could not find. `opening_on`
     * had been exposed in the view and printed on the Run card as a line on
     * a three-month exhibition — which is exactly the wrong shape for it. An
     * opening is a party on one night: you are free or you are not, and by
     * the following week the information is worthless. Folded into
     * Exhibitions it sorted with shows that run until January and was never
     * anywhere near the top.
     *
     * A lane of its own rather than a row inside Exhibitions, for the reason
     * `selection.md` gives for lanes generally: a high-volume category
     * starves a low-volume one, and 211 exhibitions against 40-odd openings
     * is exactly that. A lane also makes the absence visible, which the
     * empty-lane convention is for — a quiet fortnight in the galleries
     * should look quiet, not look like a bug.
     *
     * Matched before Exhibitions, which used to claim `type = 'opening'`.
     */
    id: 'openings', label: 'Openings', quota: 3,
    match: e => e.type === 'opening',
    missing: 'No vernissages in the next few weeks.',
    // Unranked for the reason Exhibitions is: the profile is silent on
    // visual art and said so on 29 of these rows. Soonest first is the
    // honest order for a night you either can or cannot make, and it is
    // also the urgent one — an opening a fortnight out is still a decision
    // and one tomorrow is nearly not.
    unranked: true,
  },
  {
    id: 'exhibitions', label: 'Exhibitions', quota: 3,
    // Gallery mail never reaches this match: `GALLERY_MAIL` claims it first.
    // Not a festival or a market, whatever it is about: a design fair is a
    // headline event with a score (`notRanked` makes the same exception), and
    // Markets and fairs below claims it. Until 4 Oct 2026 a `market / design`
    // row landed here, unranked, on the strength of its discipline alone.
    match: e => is(e, ['exhibition'], ['visual_art', 'photography', 'design', 'architecture'])
      && e.type !== 'market' && e.type !== 'festival',
    missing: 'Nothing on.',
    // Not scored, for the reason theatre is not: the profile is silent on it
    // and said so itself on 29 of these rows. Matches `notRanked` in
    // src/score.ts — if the two drift, rows land in a lane that sorts by score
    // with no score to sort by. A run is one decision, so soonest-first here
    // means "closing soonest" as much as "opening next", which is the right
    // urgency for a show that has been up for two months.
    unranked: true,
  },
  {
    id: 'film', label: 'Film', quota: 2,
    // Not a release: every live release is `discipline: 'film'`, so this
    // claimed all 107 ahead of the Releases lane below and ranked them by a
    // score `notRanked` never gives them. Orchestral's guard, for its reason.
    match: e => is(e, ['screening'], ['film']) && !UNSCORED_TYPES.includes(e.type),
    missing: 'No screenings.',
  },
  {
    // A release date is a different decision from a screening, for the same
    // reason Openings is its own lane and not a row inside Exhibitions: a
    // film with a date and no showtime yet is not a night out you can plan,
    // it is a note to put on the calendar before the showtimes even exist.
    // Doug: "it's enough to know the date... so I can schedule it."
    id: 'releases', label: 'Coming to cinemas', quota: 3,
    match: e => e.type === 'release',
    missing: 'Nothing newly announced.',
    // Matches `notRanked` in src/score.ts. There is no showing yet, so
    // soonest-first is the only order that makes sense.
    unranked: true,
  },
  {
    // Phase 5. Theatre and dance, and the reason the lane is small: only work
    // Doug can follow reaches it. `english_access` does that filtering — the
    // houses surtitle particular nights, so a production plays here as the run
    // of its English evenings and the German run of the same production is in
    // `entries` and never drawn. See src/harvest/theatre.ts.
    //
    // Opera is also `type = 'performance'` and is matched by Orchestral first,
    // which is why this reads the discipline as well as the type rather than
    // either on its own.
    id: 'stage', label: 'Stage', quota: 2,
    match: e => e.type === 'performance' && !!e.discipline && ['theatre', 'dance'].includes(e.discipline),
    missing: 'Nothing surtitled in English.',
    // Matches `notRanked` in src/score.ts exactly. If the two ever drift, rows
    // land in a lane that sorts by score with no score to sort by.
    unranked: true,
  },
  {
    // Blind spots K11/K4: SO36 writes the nights and A1 scores them, so the
    // lane is drawn and ranked. Any discipline — SO36's are all `other`.
    // Nightlife N7: a bar or listening night (`night.format`) is claimed
    // here too — it is a `club_night` — but only fills the quota a club
    // night leaves empty (`clubLanePicks`, below).
    id: 'club', label: 'Club', quota: 3,
    match: e => e.type === 'club_night',
    missing: 'No club nights on.',
  },
  {
    // A gig is a concert that is not classical (README, *Categories*): the
    // halls KULTURpur and the own-site recipes list. Scored since A1; the
    // deterministic gate there drops the comedy and the tribute acts.
    id: 'gigs', label: 'Gigs', quota: 2,
    // A music festival is this lane's too (30 Sept); the rest of the
    // festivals are Markets and fairs', below.
    match: e => (e.type === 'concert' || e.type === 'festival') && !!e.discipline && GIG_DISCIPLINES.includes(e.discipline),
    missing: 'No gigs on.',
  },
  {
    id: 'talks', label: 'Talks and tours', quota: 3,
    match: e => is(e, ['talk', 'tour', 'workshop'], ['literature', 'history']),
    missing: 'Nothing on.',
  },
  {
    // 30 Sept: a festival (a street fair, a garden day, Tag der Clubkultur)
    // is this lane's kind of thing; it had fallen to REST and read as "Talk".
    id: 'markets', label: 'Markets and fairs', quota: 2,
    match: e => is(e, ['market', 'festival'], ['food']),
    missing: 'No market source yet.',
  },
]

/** Anything no lane claims. Without it a third of the real data is invisible,
 *  which is worse than a lane selection.md did not anticipate. */
export const REST: Lane = {
  id: 'rest', label: 'Everything else', quota: 4,
  match: () => true,
  missing: 'Nothing left over.',
}

/**
 * What `type=news` actually holds, which is not news.
 *
 * All seven rows are open calls, prizes, fellowships, a booklet, a campaign,
 * and two recaps of things that already happened — and every one carries the
 * issue date as `published_on`, because the extractor had no real date to use.
 * `news` is functioning as the bin for any block that is not an event.
 *
 * Calling the lane Announcements is the honest description of the contents,
 * not a fix. The fix is upstream, in the extraction schema, and is on the
 * list in NEXT.md. Last, because none of it is a thing you can go to.
 */
export const NEWS: Lane = {
  id: 'news', label: 'Announcements', quota: 3,
  match: e => e.type === 'news',
  missing: 'Nothing announced.',
}

/** Every lane, in the order they are matched. Announcements is tested first so
 *  that a prize with a discipline does not land in Exhibitions, and displayed
 *  last because none of it is a thing you can go to. */
/**
 * A gallery's own mail about its show (blind spots K6, D7: keep, low
 * prominence): drawn in Browse and on the place page, never in the Feed —
 * not in Exhibitions' picks, and not in Everything else either, which would
 * be more prominent than Exhibitions rather than less.
 *
 * Not every row with the tag. N3 tags everything from a gallery sender, and
 * the opening from the same mail is the point of it (D2: "the opening is the
 * point") — an `opening` card, in Openings. So: the show, whatever its
 * discipline (an exhibition tagged `history` would otherwise reach Talks,
 * which is why this is matched ahead of every lane), and any other tagged
 * row no lane claims, which would otherwise be Everything else's.
 */
export const GALLERY_MAIL: Lane = {
  id: 'gallery-mail', label: 'Gallery mail', quota: 0,
  match: e => galleryTagged(e) && (e.type === 'exhibition' || !LANES.some(l => l.match(e))),
  missing: '',
  unranked: true,
  offFeed: true,
}

/** The Feed's own test for K6, for `Today.tsx`, which has no lanes to ask. */
export const offFeed = (e: FeedRow) => !!laneOf(e).offFeed

const MATCH_ORDER = [NEWS, GALLERY_MAIL, ...LANES, REST]

/** The order lanes are shown and offered in the filter.
 *
 *  News is no longer among them: phase 6 moved it to its own table and its own
 *  section on Home, because none of it is a thing you can go to. `NEWS` stays
 *  in the match order so a newsletter-era `type = 'news'` row still lands
 *  somewhere and is still not drawn. */
export const ALL_LANES = [...LANES, REST].filter(l => !l.hidden)

/** Every lane including the hidden ones, for anything that has to account for
 *  where a row went rather than show it. */
export const EVERY_LANE = [...LANES, REST, NEWS, GALLERY_MAIL]

export const laneOf = (e: FeedRow): Lane => MATCH_ORDER.find(l => l.match(e)) ?? REST

/** Assign every row to exactly one lane, first match wins, so a total across
 *  lanes equals the number of rows and nothing is silently counted twice. */
export function intoLanes(rows: FeedRow[]): { lane: Lane; rows: FeedRow[] }[] {
  const out = EVERY_LANE.map(lane => ({ lane, rows: [] as FeedRow[] }))
  const index = new Map(EVERY_LANE.map((l, i) => [l.id, i]))
  for (const r of rows) out[index.get(laneOf(r).id)!].rows.push(r)
  // A hidden lane still claimed its rows, which is the whole point, and then
  // does not appear.
  return out.filter(l => !l.lane.hidden && !l.lane.offFeed && l.lane.id !== 'news')
}

/** How many rows the hidden lanes are holding back, for the one line that
 *  says so rather than letting them vanish silently. */
export function hiddenCount(rows: FeedRow[]): number {
  return rows.filter(r => laneOf(r).hidden || laneOf(r).id === 'news').length
}

// ---------------------------------------------------------------------------
// Sort
// ---------------------------------------------------------------------------

export const SORTS = {
  // Default, now that Stage D writes a score. selection.md's quotas are meant
  // to "surface the top few", which only means anything once there is a top —
  // before scoring existed the lane quota took the soonest few, which is a
  // different and much weaker idea.
  best: {
    label: 'Best for you',
    // A row with no score is not a bad row. Theatre carries no score by
    // design, and `(b.score ?? -1) - (a.score ?? -1)` sorted every Stage row
    // below a chamber concert the funnel had explicitly rejected with a 0.
    // Unscored rows keep their own order, soonest first, and sit after the
    // scored ones rather than beneath them.
    cmp: (a: FeedRow, b: FeedRow) => {
      // `rank` where a row has one, the score otherwise. Today only films
      // carry a rank: the score says how good a film is and says nothing
      // about whether you can go, and in a lane where ten rows share 0.55
      // that second question is the whole of the ordering. See `filmRank`.
      const ra = a.rank ?? a.score
      const rb = b.rank ?? b.score
      if (ra == null && rb == null) return sortDate(a) < sortDate(b) ? -1 : 1
      if (ra == null) return 1
      if (rb == null) return -1
      return rb - ra
    },
  },
  soonest: { label: 'Soonest', cmp: (a: FeedRow, b: FeedRow) => (sortDate(a) < sortDate(b) ? -1 : 1) },
  latest: { label: 'Latest', cmp: (a: FeedRow, b: FeedRow) => (sortDate(a) > sortDate(b) ? -1 : 1) },
  mentions: { label: 'Most mentioned', cmp: (a: FeedRow, b: FeedRow) => b.mention_count - a.mention_count },
  title: { label: 'A–Z', cmp: (a: FeedRow, b: FeedRow) => a.title.localeCompare(b.title) },
} as const

export type SortKey = keyof typeof SORTS

/** Nightlife N7: the two `night.format`s that are a bar's evening rather
 *  than a club's — a label night at a bar, an in-store, a listening session
 *  (the plan's *A bar one-off*). */
export const BAR_FORMATS: readonly string[] = ['bar', 'listening']

/** Is this a bar's night in the Club lane? `?.` on `night`: a row cached
 *  before `feed_page` sent it has no key (the E5 trap). */
export const isBarNight = (e: FeedRow): boolean =>
  e.type === 'club_night' && BAR_FORMATS.includes(e.night?.format ?? '')

/**
 * The Club lane's picks: club nights first by `best`, and a bar or listening
 * night only in a slot no club night wants — so with three club nights on,
 * the lane is three club nights whatever the bar night scored, and a bar
 * night never outranks a club night at equal score or any other. Fewer than
 * `quota` club nights, and the bars fill the rest, best first.
 *
 * The plan's *entries* section: "the Club lane can split on `format` later
 * if bar rows crowd out clubs; it is one field, read in one place." This is
 * the one place. Rows that are not club nights are ignored, so a caller can
 * pass the lane's rows or the whole feed.
 */
export function clubLanePicks(rows: readonly FeedRow[], quota = 3): FeedRow[] {
  const nights = rows.filter(e => e.type === 'club_night').sort(SORTS.best.cmp)
  const clubs = nights.filter(e => !isBarNight(e))
  const bars = nights.filter(isBarNight)
  return [...clubs, ...bars].slice(0, Math.max(0, quota))
}

// ---------------------------------------------------------------------------
// Presentation
// ---------------------------------------------------------------------------

/**
 * The one line of prose a card gets.
 *
 * Doug's call, 20 September: it says what the thing IS, not why the scorer
 * surfaced it. Both were defensible and having both is one line too many on a
 * phone, so this was measured before it was chosen — of 104 near-horizon rows
 * scoring 0.60 or better, 100 had a score reason containing scorer vocabulary
 * ("Mahler is an anchor deliberate-choice composer", "1970s decade weight
 * 0.75", "profile silent on theatre"), and on the classical lane the reason
 * restates the orchestra and the form that `detailLine` already prints.
 *
 * So the reason moves to the sheet, which already had a place for it under
 * "Why this is here", and the card gets the summary or nothing.
 *
 * Nothing, often. The harvester only writes a summary where the source
 * publishes prose and only ever one sentence of it, so a concert calendar
 * yields none — which is the point rather than a gap to fill. A generated
 * sentence about "Konzerthausorchester Berlin, Oscar Jockel" would say nothing
 * the title did not and would be a line of noise on every card. That is still
 * true of the concert lanes and nothing there is generated.
 *
 * One exception, Doug's call on 21 September and narrow: an exhibition whose
 * gallery published no text at all can carry a sentence a model found by
 * searching for the show, and only if it came back with a source page it
 * actually retrieved. `summary_source_url` is how the card knows, and
 * `generatedSummary` is what it asks. The two kinds of sentence must never
 * look alike on screen.
 */
/* `cardLine` lived here and returned `e.summary`. WP 2.4 deleted the card that
 * printed it — a row states what it knows in a meta line, not a sentence — and
 * `summary` left `feed_lite` with it, so there is nothing for this to read and
 * nobody left to call it. */

/** Is this sentence ours rather than the source's? A non-null
 *  `summary_source_url` is the only thing that says so, and it is only ever set
 *  by the web-search pass.
 *
 *  Takes the two values rather than the row: since WP 2.4 they arrive from
 *  `feed_detail` when the sheet opens, not on the row. Both, because a source
 *  URL without the sentence it sourced is the bug CLAUDE.md's list ends on. */
export function generatedSummary(summary: string | null, sourceUrl: string | null): string | null {
  return summary && sourceUrl ? sourceUrl : null
}

/**
 * The sheet's own text, where there is more of it than the card's one line.
 *
 * Phase 7a (D2): the card keeps `summary` — the first sentence — and the
 * sheet shows up to three sentences of the museum's own published text,
 * where the walk found one. Null for everything else, which leaves the
 * sheet's existing `summary` paragraph exactly as it was.
 */
export function sheetText(e: FeedRow): string | null {
  const src = e.exhibition?.text_src
  return src ? sentences(src, 3, 400) : null
}



/** The three widths a hero ever comes in. WP 1.2: an upload resizes to these
 *  in the browser and the backfill (`scripts/hero-renditions.mjs`) does the
 *  same for every hero written before the pipeline existed, so an asset key
 *  can always be asked for any of the three. */
export type HeroSize = 112 | 800 | 1600

const TMDB_SIZE: Record<HeroSize, 'w185' | 'w500' | 'w780'> = { 112: 'w185', 800: 'w500', 1600: 'w780' }

/** `ab/abc123.png` -> `ab/abc123` — the rule this file and the backfill
 *  script both have to apply the same way, or a rendition lands at a key
 *  nothing ever asks for. Strips only the last extension after the last
 *  slash, so a key with two dots keeps the first. */
function keyWithoutExt(key: string): string {
  const slash = key.lastIndexOf('/')
  const dot = key.lastIndexOf('.')
  return dot > slash ? key.slice(0, dot) : key
}

/**
 * The hero image URL, at one of the three sizes, or null.
 *
 * Most entries have none and always will: only four images have ever been
 * captured, and matching is deliberately unwilling to guess. The swatch
 * below is the normal case, not the fallback.
 *
 * An asset key (`hero_key`/`venue_hero_key`) never resolves to the original
 * file any more — WP 1.2 moved encoding out of the Worker (CLAUDE.md: CPU is
 * the binding constraint) and onto the browser at upload and onto
 * `scripts/hero-renditions.mjs` for everything uploaded before it, so every
 * asset key now has three WebP siblings sitting next to the original one.
 * `heroFallbackUrl` is the original, for a browser whose `onError` finds a
 * rendition missing.
 */
export function heroUrl(e: FeedRow, size: HeroSize = 800): string | null {
  // A film's poster before anything else: it is a picture of the actual thing,
  // where a venue hero is a picture of the room it is shown in.
  const poster = posterUrl(e, TMDB_SIZE[size])
  if (poster) return poster

  // The row's own picture, for the same reason — where the source lets us
  // show it.
  //
  // `image_url` has been filled since phase 4 and read by nothing: this
  // function went poster → `hero_key` → `venue_hero_key` and stopped, so an
  // exhibition with a picture of the work showed a picture of the building.
  // The harvest now captures 144 exhibition pictures and 115 credits off
  // INDEX, which is the fact that was missing.
  //
  // **INDEX refuses to serve them cross-site.** The same URL that returns
  // 180KB of JPEG to a request with no referer returns 403 to one sent from
  // another origin — hotlink protection, checked by hand on 21 September.
  // That is a refusal, and rule 4 says a refusal is reported rather than
  // worked around: stripping the referer to get the bytes is exactly the
  // kind of routing-around the rule names. Copying them into R2 avoids the
  // refusal by not triggering it, which is a rights judgement about someone
  // else's press images and is Doug's, not mine — the venue heroes are
  // re-hosted because Commons licences say they may be, and a courtesy line
  // is not a licence. It is in NEXT.md under Needs Doug.
  //
  // Doug said re-host, on 25 September. The Worker now copies each refused
  // picture into R2 (src/harvest/rehost.ts) and points `hero_asset_id` at
  // it, so the same row reaches `hero_key` below and is drawn from our own
  // origin with its own credit. `HOTLINK_REFUSED` still stops the browser
  // asking INDEX directly, which is the part that was a refusal.
  //
  // No rendition exists for this one — the source is `image_url` itself, a
  // hotlinked file we do not control the size of.
  if (ownPictureUsable(picturesOf(e))) return e.image_url!

  const base = import.meta.env.VITE_ASSET_BASE_URL
  const key = e.hero_key ?? e.venue_hero_key
  if (!base || !key) return null
  return `${String(base).replace(/\/+$/, '')}/${keyWithoutExt(key)}-${size}.webp`
}

/** The `srcset` for an asset-key hero, or null where there is nothing to
 *  offer a size for — a poster and a hotlinked `image_url` are one file at
 *  one size, and `sizes`/`srcset` on a single candidate is nothing to a
 *  browser but noise. */
export function heroSrcSet(e: FeedRow): string | null {
  if (posterUrl(e)) return null
  if (ownPictureUsable(picturesOf(e))) return null

  const base = import.meta.env.VITE_ASSET_BASE_URL
  const key = e.hero_key ?? e.venue_hero_key
  if (!base || !key) return null

  const b = String(base).replace(/\/+$/, '')
  const stem = keyWithoutExt(key)
  return `${b}/${stem}-112.webp 112w, ${b}/${stem}-800.webp 800w, ${b}/${stem}-1600.webp 1600w`
}

/** The original file, for an `<img onError>` to fall back to once a
 *  requested rendition 404s — a row uploaded mid-migration, or a backfill
 *  that has not reached this key yet. Rule 2: the reader never assumes a
 *  rendition exists; this is the one place it assumes the original still
 *  does, which every hero has always had. */
export function heroFallbackUrl(e: FeedRow): string | null {
  const poster = posterUrl(e)
  if (poster) return poster
  if (ownPictureUsable(picturesOf(e))) return e.image_url!

  const base = import.meta.env.VITE_ASSET_BASE_URL
  const key = e.hero_key ?? e.venue_hero_key
  if (!base || !key) return null
  return `${String(base).replace(/\/+$/, '')}/${key}`
}

/** The line that has to appear under a picture, where one does.
 *
 *  Returns the row's own credit for its own picture, and the venue's for the
 *  venue photograph — the two are different rights and the card must not
 *  print one under the other.
 *
 *  Reads `heroFallbackUrl` rather than `heroUrl`: `creditFor` (attribution.ts,
 *  dependency-free on purpose) tells the row's own picture apart from the
 *  venue's by checking whether the drawn `src` ends in `hero_key` — true of
 *  the original, and no longer true of a `-800.webp` rendition. Which credit
 *  belongs to the picture does not depend on which size of it is on screen,
 *  so the original's URL is the right thing to ask that question with. */
export function heroCredit(e: FeedRow): string | null {
  return creditFor(heroFallbackUrl(e), picturesOf(e), posterUrl(e))
}

const picturesOf = (e: FeedRow): Pictures => ({
  imageUrl: e.image_url,
  ownKey: e.hero_key,
  imageCredit: e.image_credit,
  venueCredit: e.venue_hero_credit,
  venueLicense: e.venue_hero_license,
})

/** TMDb's poster, at a size a card can use.
 *
 *  Hotlinked from image.tmdb.org, which is what TMDb's terms contemplate —
 *  they ask for attribution rather than for re-hosting, and the file is
 *  already on a CDN. `w500` is about twice a card's poster slot, which is
 *  right for a retina phone and still small; `w185`/`w780` are `heroUrl`'s
 *  112/1600 asked in TMDb's own size names. */
export function posterUrl(e: FeedRow, size: 'w185' | 'w500' | 'w780' = 'w500'): string | null {
  const path = e.film?.poster_path
  return typeof path === 'string' && path ? `https://image.tmdb.org/t/p/${size}${path}` : null
}

/** Rotten Tomatoes and Metacritic, where OMDb had them. Returns an empty list
 *  rather than a placeholder: a film with no score should say nothing about
 *  scores, not "n/a". */
export function filmScores(e: FeedRow): { label: string; value: string }[] {
  const f = e.film ?? {}
  const out: { label: string; value: string }[] = []
  if (typeof f.rt_score === 'number') out.push({ label: 'RT', value: `${f.rt_score}%` })
  if (typeof f.metascore === 'number') out.push({ label: 'Metacritic', value: String(f.metascore) })
  if (typeof f.imdb_rating === 'number') out.push({ label: 'IMDb', value: f.imdb_rating.toFixed(1) })
  return out
}

/** Is this picture of the event, or of the room it happens in?
 *
 *  The card mockup labels a venue photograph with the venue's name, because a
 *  photograph of the Philharmonie above a Bruckner programme is scene-setting
 *  and would be a lie if it were presented as a picture of the concert. */
export const heroIsVenue = (e: FeedRow) =>
  !e.hero_key && !ownPictureUsable(picturesOf(e)) && !!e.venue_hero_key

export const heroAlt = (e: FeedRow) =>
  e.hero_key ? (e.hero_alt ?? '') : (e.venue_hero_alt ?? e.venue ?? '')

/** No entry has an image: nothing has ever been written to `hero_asset_id`,
 *  and the assets bucket has no public domain in front of it yet. A stable
 *  colour per entry at least makes a card recognisable between visits. */
export function hue(id: string): number {
  // Hashed into a wide space and then spread around the wheel by a step
  // coprime with 360. Taking the modulo at 360 directly clusters ids that
  // share a prefix — the two Fotografiska entries came out the same shade of
  // tan — and the colour is the only thing distinguishing one card from the
  // next while no entry has an image.
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 131 + id.charCodeAt(i)) % 100003
  return (h * 137) % 360
}

export const swatch = (id: string) =>
  `linear-gradient(135deg, hsl(${hue(id)} 42% 74%), hsl(${(hue(id) + 34) % 360} 40% 54%))`

export function priceText(e: FeedRow): string | null {
  // UI-audit a1-10: one rule with the row's, so a placeholder zero is not
  // "€0" here while the row prints nothing.
  return priceFact(e)
}

/**
 * Phase 8a: the compact card's "from <source>" line for a promoted
 * newsletter lead.
 *
 * `organisations.name` where `mail_source` matches `organisations.mail_source`
 * would read better where one exists — a promoted lead sets no
 * `organisation_id`, though, so that lookup needs the organisations table
 * loaded on every screen that draws a card, which is more than this small a
 * change should carry. The fallback below is the one the app already had:
 * Saved.tsx folds a lead's own `mail_source` the same way for the row
 * awaiting a decision. Worth revisiting once a screen already holds the
 * organisations list for another reason.
 */
export function newsletterSource(e: FeedRow): string | null {
  const src = e.newsletter?.mail_source
  if (!src) return null
  return src.includes('@') ? (src.split('@')[1] ?? src) : src
}

/** The raw enum value with its underscore swapped for a space and nothing
 *  else — deliberately, because `EventSheet`'s "type · form" tag wants it
 *  exactly this lowercase (H2's modify). A caller that needs a capital
 *  does its own: WP 2.7 found `Feed`'s lane header had been relying on
 *  `.lanehead`'s `text-transform: uppercase` for the capital, not just the
 *  tracked-eyebrow look, so removing that CSS for D3 also dropped the
 *  capital — this file's own recurring bug, one value doing two jobs. */
export const typeLabel = (t: string) => t.replace(/_/g, ' ')

/** The heading for a type's own page (`#/feed/<type>`) — plural and led
 *  with a capital, where `typeLabel` alone is the raw enum value with its
 *  underscore swapped for a space and nothing else. `FeedType`'s `<h1>`
 *  has no other treatment, so the page ended up titled "exhibition" or
 *  "screening" without this (UI-audit F19). Every value this actually
 *  takes — concert, opening, exhibition, screening, release, performance,
 *  talk, tour, workshop, market, club_night — pluralises by adding an s;
 *  there is no irregular one to special-case yet. */
export const typeTitle = (t: string) => {
  const label = typeLabel(t)
  const plural = label.endsWith('s') ? label : `${label}s`
  return plural.charAt(0).toUpperCase() + plural.slice(1)
}

export const today = () => iso(new Date())

/** Saturday and Sunday of the week today falls in; if today is already the
 *  weekend, this weekend is the one in progress. */
export function thisWeekend(from = today()): [string, string] {
  const dow = D(from).getUTCDay()             // 0 Sun … 6 Sat
  const toSat = dow === 0 ? -1 : 6 - dow
  const sat = iso(new Date(D(from).getTime() + toSat * 86400000))
  const sun = iso(new Date(D(sat).getTime() + 86400000))
  return [sat, sun]
}

// ---------------------------------------------------------------------------
// One card per film
// ---------------------------------------------------------------------------

/**
 * Merge the weekly runs of one film into a single row.
 *
 * Doug's prototype: "One card per film, not per cinema. Three cards for the
 * same film was the worst thing in the old feed." `filmRunKey` already took
 * the cinema out of the key, which fixed the per-cinema half — a film playing
 * at four cinemas in one week is one entry with showings at each.
 *
 * The half it did not fix is time. A cinema listing is pulled twice a week and
 * only ever publishes its own week, so the grouper never sees two weeks at
 * once and the 14-day gap rule has nothing to join. VATERLAND is three
 * entries: `vaterland|2026-09-17`, `|2026-09-24`, `|2026-10-01`.
 *
 * This is done here rather than by changing the run key on purpose. The key is
 * identity: changing it orphans every film row the recipe has ever written,
 * and an orphaned row is cancelled by the next complete run. The week is also
 * a real unit — DIRECTION's own refresh table calls cinemas "decided the week
 * of". So the entries stay as they are and the *card* is what merges, which is
 * what the prototype was asking for in the first place.
 *
 * The earliest run is the representative and carries the group's ids, so save
 * and dismiss act on all of them. Dismissing one week of a film and being
 * shown the next two is not a thing anyone wants.
 *
 * **Since cards WP3 this is a safety net, and usually a no-op.** The views
 * now drop twins and compute a canonical's dates and `places` over its whole
 * set, so a film the twins pass has joined arrives as one row and there is
 * nothing here to merge. It stays because two canonicals of one film can
 * still exist: an unmatched film (no `work_id`) that the twins rule never
 * paired, and — until cards WP1 takes the week out of the run key — the days
 * when next week's listings are up beside this week's. Where it does merge,
 * the card's `places` are the members' merged (`mergePlaces`), so the where
 * line names every cinema the merged card stands for, not the head's alone.
 */
export function mergeFilmRuns(
  rows: FeedRow[],
  /** Every showing the screen holds, so a merged card can be ranked on the
   *  shape of the whole run rather than on one week of it. Optional: a
   *  screen with no showings to hand gets the merge and no ranking, which is
   *  the old behaviour. */
  showings?: { entry_id: string | null; starts_at: string | null;
               venue_id?: string | null; building_id?: string | null }[],
  today = iso(new Date()),
): FeedRow[] {
  const out: FeedRow[] = []
  const byFilm = new Map<string, FeedRow[]>()

  // Showings by entry, so ranking a card is a lookup rather than a scan.
  const byEntry = new Map<string, NonNullable<typeof showings>>()
  for (const s of showings ?? []) {
    if (!s.entry_id) continue
    const list = byEntry.get(s.entry_id)
    if (list) list.push(s); else byEntry.set(s.entry_id, [s])
  }
  // The group's days are read in its head row's zone: a film card is one
  // town's cinemas (C2.1).
  const rankFor = (ids: string[], score: number | null | undefined, tz: string): number | null => {
    if (!showings) return null
    return filmRank(score ?? null, runShape(ids.flatMap(id => byEntry.get(id) ?? []), today, tz))
  }

  for (const r of rows) {
    const isFilm = r.discipline === 'film' || r.type === 'screening'
    if (!isFilm) { out.push(r); continue }
    // Phase 6c: `work_id` first — see `filmGroupKey`. A film with no
    // `work_id` still keys on title and access, so an OmU screening and a
    // German-dubbed one of an unmatched film stay the two different
    // evenings for Doug they always were.
    const key = filmGroupKey(r)
    const bucket = byFilm.get(key)
    if (bucket) bucket.push(r); else byFilm.set(key, [r])
  }

  for (const group of byFilm.values()) {
    const sorted = [...group].sort((a, b) => (sortDate(a) < sortDate(b) ? -1 : 1))
    const head = sorted[0]
    if (sorted.length === 1) {
      out.push({ ...head, rank: rankFor([head.id], head.score, zoneOfCity(head.city)) ?? undefined })
      continue
    }

    const ends = sorted.map(r => r.run_end ?? r.run_start).filter(Boolean) as string[]
    const nexts = sorted.map(r => r.next_showing).filter(Boolean) as string[]

    out.push({
      ...head,
      group_ids: sorted.map(r => r.id),
      run_end: ends.length ? ends.reduce((a, b) => (a > b ? a : b)) : head.run_end,
      next_showing: nexts.length ? nexts.reduce((a, b) => (a < b ? a : b)) : head.next_showing,
      showing_count: sorted.reduce((n, r) => n + (r.showing_count ?? 0), 0),
      // The best of the group rather than the first: a later week's row may
      // have been the one TMDb reached.
      score: sorted.reduce<number | null>((best, r) =>
        r.score != null && (best == null || r.score > best) ? r.score : best, null),
      mention_count: Math.max(...sorted.map(r => r.mention_count ?? 0)),
      // Cards WP4. Undefined when any member was cached before `places`
      // existed — the where line then falls back to the showings, as before.
      places: mergePlaces(sorted.map(r => r.places)),
      // Over the whole merged run, not one week of it: a film in its third
      // week at six cinemas is exactly the case this is for.
      rank: rankFor(sorted.map(r => r.id), sorted.reduce<number | null>((best, r) =>
        r.score != null && (best == null || r.score > best) ? r.score : best, null), zoneOfCity(head.city)) ?? undefined,
    })
  }

  return out
}

/** Every entry id a card stands for. One, unless films were merged. */
export const idsOf = (e: FeedRow): string[] => e.group_ids ?? [e.id]

/**
 * What a save or a dismissal records about the row, for the taste signal.
 *
 * Was written out inline in Feed and again in Saved, and Venues would have
 * been a third copy. The scorer reads these, so three definitions drifting
 * apart is three different ideas of what a signal means.
 */
export const signalFeatures = (e: FeedRow) => ({
  type: e.type,
  discipline: e.discipline,
  organisation: e.organisation,
  english_access: e.english_access,
  tags: e.tags,
  ...affinityIds(e),
})

/**
 * Has everything this row offered already happened?
 *
 * The feed was date-only, so at eleven on a Sunday night it still showed a
 * concert that started at seven that evening — a row that cannot be acted on,
 * sitting at the top of the day it belongs to.
 *
 * `next_showing` is the view's own answer: the earliest showing still in the
 * future, computed server-side against `now()`. So a row with showings, none
 * of them ahead, and nothing left to run is over. Each clause earns its place:
 *
 *   showing_count > 0   an exhibition has no showings and is not over because
 *                       of it; its run dates say when it ends.
 *   next_showing null   nothing ahead. This is the time-aware half.
 *   run_end <= today    and it is not a run that continues tomorrow, which is
 *                       the case for a film week whose showings the view has
 *                       not caught up with.
 *
 * **A festival or a market is the exception** (`daylong.ts`): it is over when
 * its last day is, `run_end < today`, and not when its last showing started.
 * A lead with no stated time is written as a `T00:00` showing, so the rule
 * above dropped a two-day festival at 00:01 on its last day, and the Kürbisfest
 * (a stated 11:00) at 11:01 with the stalls open all afternoon.
 */
export function alreadyHappened(e: FeedRow, today: string): boolean {
  const daylong = daylongHasEnded(e, today)
  if (daylong !== null) return daylong
  if (!(e.showing_count ?? 0)) return false
  if (e.next_showing) return false
  const last = e.run_end ?? e.run_start
  return Boolean(last && last <= today)
}
